import type { BarPoint } from '../../api/marketData'
import type {
  IndicatorContext,
  IndicatorOutput,
  IndicatorLine,
  IndicatorPriceLevel,
  IndicatorBand,
  IndicatorLabel,
  UserIndicator,
  UserIndicatorConfig,
  UserEmaIndicator,
  UserOrbIndicator,
  UserPrevDayHighLowIndicator,
  UserPremarketHighLowIndicator,
  UserSmaIndicator,
  UserVwapIndicator,
} from './types'
import { ema, sma, hlc3 } from './math'
import {
  nyDateKey,
  nyCalendarDate,
  toNYHours,
  RTH,
  PREMARKET,
  OPENING_RANGE,
} from './session'
import { DEFAULT_INDICATOR_COLORS } from './defaultPreset'
import { intervalToBucket } from './types'

// ── helpers ──────────────────────────────────────────────────────────────────

type IndicatorStyle = NonNullable<IndicatorLine['style']>
const PRICE_LEVEL_RIGHT_EXTENSION_BARS = 5

function intervalDurationMs(interval: IndicatorContext['interval']): number {
  switch (interval) {
    case '1s': return 1_000
    case '1m': return 60_000
    case '5m': return 5 * 60_000
    case '15m': return 15 * 60_000
    case '1h': return 60 * 60_000
    case '1d': return 24 * 60 * 60_000
    case '1w': return 7 * 24 * 60 * 60_000
  }
}

function extendedPriceLevelToT(context: IndicatorContext): number {
  const lastBar = context.bars[context.bars.length - 1]
  const anchorT = lastBar?.t ?? context.nowMs
  return anchorT + intervalDurationMs(context.interval) * PRICE_LEVEL_RIGHT_EXTENSION_BARS
}

function sessionStartMinutes(sessionRange: { start: [number, number]; end: [number, number] }): number {
  return sessionRange.start[0] * 60 + sessionRange.start[1]
}

function sessionEndMinutes(sessionRange: { start: [number, number]; end: [number, number] }): number {
  return sessionRange.end[0] * 60 + sessionRange.end[1]
}

function barStartMinutes(bar: BarPoint): number {
  const { h, m } = toNYHours(bar.t)
  return h * 60 + m
}

function barEndMinutes(bar: BarPoint, interval: IndicatorContext['interval']): number {
  return barStartMinutes(bar) + intervalDurationMs(interval) / 60_000
}

function barOverlapsSession(
  bar: BarPoint,
  interval: IndicatorContext['interval'],
  sessionRange: { start: [number, number]; end: [number, number] },
): boolean {
  return barStartMinutes(bar) < sessionEndMinutes(sessionRange) &&
    barEndMinutes(bar, interval) > sessionStartMinutes(sessionRange)
}

function hasCompleteSessionCoverage(
  dayBars: BarPoint[],
  interval: IndicatorContext['interval'],
  sessionRange: { start: [number, number]; end: [number, number] },
): boolean {
  const sessionBars = dayBars
    .filter((bar) => barOverlapsSession(bar, interval, sessionRange))
    .sort((a, b) => a.t - b.t)
  if (sessionBars.length === 0) return false
  const first = sessionBars[0]
  const last = sessionBars[sessionBars.length - 1]
  return barStartMinutes(first) <= sessionStartMinutes(sessionRange) &&
    barEndMinutes(first, interval) > sessionStartMinutes(sessionRange) &&
    barStartMinutes(last) < sessionEndMinutes(sessionRange) &&
    barEndMinutes(last, interval) >= sessionEndMinutes(sessionRange)
}

/** Convert a single numeric MA result to an IndicatorLine. */
function maToLine(
  id: string,
  title: string,
  values: (number | null)[],
  bars: BarPoint[],
  color: string,
  width: number,
  style?: IndicatorStyle,
): IndicatorLine | null {
  const points: Array<{ t: number; value: number }> = []
  for (let i = 0; i < values.length; i++) {
    const v = values[i]
    if (v !== null && v !== undefined && isFinite(v)) {
      points.push({ t: bars[i].t, value: v })
    }
  }
  if (points.length === 0) return null
  return { id, title, color, width, style, points }
}

/** Check if a bar's timestamp falls within a session range. */
function barInSession(bar: BarPoint, sessionRange: { start: [number, number]; end: [number, number] }): boolean {
  const { h, m } = toNYHours(bar.t)
  const currentMinutes = h * 60 + m
  const [startH, startM] = sessionRange.start
  const [endH, endM] = sessionRange.end
  const startMinutes = startH * 60 + startM
  const endMinutes = endH * 60 + endM
  return currentMinutes >= startMinutes && currentMinutes < endMinutes
}

/** Create a finite horizontal price level at a given value. */
function makePriceLevel(
  id: string,
  title: string,
  value: number | null,
  color: string,
  width: number,
  fromT: number,
  toT: number,
  style?: IndicatorStyle,
): IndicatorPriceLevel | null {
  if (value === null || !isFinite(value)) return null
  if (!Number.isFinite(fromT) || !Number.isFinite(toT) || toT <= fromT) return null
  return {
    id,
    title,
    color,
    width,
    style: style ?? 'dashed',
    segments: [{ fromT, toT, value }],
  }
}

function scopedMovingAverageLineId(
  indicator: UserEmaIndicator | UserSmaIndicator,
  bucket: string,
): string {
  const canonicalPrefix = `${indicator.type}-${indicator.period}-`
  const bucketSuffix = `-${bucket}`
  if (indicator.id.startsWith(canonicalPrefix) && indicator.id.endsWith(bucketSuffix)) {
    return indicator.id
  }
  return `${indicator.type}-${indicator.period}-${bucket}-${indicator.id}`
}

function scopedVwapLineId(indicator: UserVwapIndicator, bucket: string): string {
  const bucketSuffix = `-${bucket}`
  if (indicator.id.startsWith('vwap-') && indicator.id.endsWith(bucketSuffix)) {
    return indicator.id
  }
  return `vwap-${bucket}-${indicator.id}`
}

// ── computeEmaSma ────────────────────────────────────────────────────────────

export function computeEmaSma(
  context: IndicatorContext,
  config: UserIndicatorConfig,
): IndicatorLine[] {
  const { bars, treatLastBarAsClosed } = context
  let values = bars
  if (treatLastBarAsClosed && bars.length > 0) {
    values = bars.slice(0, -1)
  }
  if (values.length === 0) return []

  const closeValues = values.map(b => b.c)
  const lines: IndicatorLine[] = []
  const bucket = intervalToBucket(context.interval)

  for (const ind of config.indicators) {
    if (!ind.enabled) continue
    if (ind.type !== 'ema' && ind.type !== 'sma') continue

    const maValues = ind.type === 'ema'
      ? ema(closeValues, ind.period)
      : sma(closeValues, ind.period)

    const title = `${ind.type.toUpperCase()}(${ind.period})`
    const line = maToLine(
      scopedMovingAverageLineId(ind, bucket),
      title,
      maValues,
      bars,
      ind.color,
      ind.width,
      ind.style,
    )
    if (line) lines.push(line)
  }

  return lines
}

// ── computeVwap ──────────────────────────────────────────────────────────────

export function computeVwap(
  context: IndicatorContext,
  config: UserIndicatorConfig,
): IndicatorLine | null {
  const vwapInds = config.indicators.filter(
    (i: UserIndicator): i is UserVwapIndicator => i.type === 'vwap' && i.enabled,
  )
  if (vwapInds.length === 0) return null

  const ind = vwapInds[0]
  const { bars } = context

  const volumes = bars.map(b => b.v)

  // Cumulative VWAP
  const vwapValues: (number | null)[] = []
  let cumPriceVol = 0
  let cumVol = 0
  let lastDate: string | null = null

  // Anchor at NY calendar-day boundary (midnight ET). Premarket, RTH, and
  // after-hours bars on the same calendar day share one cumulative VWAP — this
  // matches TradingView's default when extended hours are visible. Using the
  // trading-day rule (which rolls at 09:30 ET) would drag the prior day's
  // after-hours and the next day's premarket into the same session.
  for (let i = 0; i < bars.length; i++) {
    const date = nyCalendarDate(bars[i].t)
    if (date !== lastDate) {
      cumPriceVol = 0
      cumVol = 0
      lastDate = date
    }

    if (volumes[i] === 0) {
      vwapValues.push(cumVol > 0 ? cumPriceVol / cumVol : null)
      continue
    }

    const tp = ind.source === 'close' ? bars[i].c : hlc3(bars[i])
    cumPriceVol += tp * volumes[i]
    cumVol += volumes[i]
    vwapValues.push(cumPriceVol / cumVol)
  }

  const points: Array<{ t: number; value: number }> = []
  for (let i = 0; i < vwapValues.length; i++) {
    const v = vwapValues[i]
    if (v !== null && isFinite(v)) {
      points.push({ t: bars[i].t, value: v })
    }
  }
  if (points.length === 0) return null

  return {
    id: scopedVwapLineId(ind, intervalToBucket(context.interval)),
    title: `VWAP (${ind.source})`,
    color: ind.color,
    width: ind.width,
    style: ind.style,
    points,
  }
}

export function computePremarketHighLow(
  context: IndicatorContext,
  userConfig: UserIndicatorConfig,
): IndicatorPriceLevel[] | null {
  const { bars, nowMs } = context

  // Determine "today" from current time.
  const todayCalDate = nyCalendarDate(nowMs)

  // Find the most recent calendar date that has premarket bars (today or earlier).
  // At off-hours before premarket starts (e.g. 01:00 AM), this falls back to
  // the last completed trading day's premarket.
  let targetCalDate: string | null = null
  const dates = [...new Set(bars.map(b => nyCalendarDate(b.t)))].sort().reverse()
  for (const d of dates) {
    if (d > todayCalDate) continue // skip future dates
    if (bars.some(b => nyCalendarDate(b.t) === d && barInSession(b, PREMARKET))) {
      targetCalDate = d
      break
    }
  }
  if (!targetCalDate) return null

  // Only show if the chart is showing today or that target date.
  const currentCalDate = bars.length > 0 ? nyCalendarDate(bars[bars.length - 1].t) : todayCalDate
  if (currentCalDate !== todayCalDate && currentCalDate !== targetCalDate) return null

  let premarketHigh: number | null = null
  let premarketLow: number | null = null
  for (const bar of bars) {
    if (nyCalendarDate(bar.t) !== targetCalDate) continue
    if (!barInSession(bar, PREMARKET)) continue
    if (premarketHigh === null || bar.h > premarketHigh) premarketHigh = bar.h
    if (premarketLow === null || bar.l < premarketLow) premarketLow = bar.l
  }

  // Pick color / width / style from user config; fall back to defaults.
  const preIndicator = userConfig.indicators.find(
    (i) => i.type === 'premarketHighLow',
  ) as UserPremarketHighLowIndicator | undefined
  const highColor = preIndicator?.highColor ?? DEFAULT_INDICATOR_COLORS.premarketHighLow
  const lowColor = preIndicator?.lowColor ?? DEFAULT_INDICATOR_COLORS.premarketHighLow
  const width = preIndicator?.width ?? 1
  const style = preIndicator?.style ?? 'dashed'

  // Build finite segments from the actual premarket bars through a small
  // right-side extension past the latest loaded bar.
  let minBarT: number | null = null
  let maxBarT: number | null = null
  for (const bar of bars) {
    if (nyCalendarDate(bar.t) !== targetCalDate) continue
    if (!barInSession(bar, PREMARKET)) continue
    if (minBarT === null || bar.t < minBarT) minBarT = bar.t
    if (maxBarT === null || bar.t > maxBarT) maxBarT = bar.t
  }
  if (minBarT === null || maxBarT === null) return null
  const segmentFrom = minBarT
  const segmentTo = Math.max(maxBarT, extendedPriceLevelToT(context))

  const levels: IndicatorPriceLevel[] = []
  if (premarketHigh !== null && isFinite(premarketHigh)) {
    levels.push({
      id: 'premarket-high', title: 'Premarket High', color: highColor,
      width, style,
      segments: [{ fromT: segmentFrom, toT: segmentTo, value: premarketHigh }],
    })
  }
  if (premarketLow !== null && isFinite(premarketLow)) {
    levels.push({
      id: 'premarket-low', title: 'Premarket Low', color: lowColor,
      width, style,
      segments: [{ fromT: segmentFrom, toT: segmentTo, value: premarketLow }],
    })
  }
  return levels.length > 0 ? levels : null
}

// ── computePrevDayHighLow ────────────────────────────────────────────────────

export function computePrevDayHighLow(
  context: IndicatorContext,
  userConfig: UserIndicatorConfig,
): IndicatorPriceLevel[] | null {
  const { bars, nowMs, prevDayBars } = context
  const bucket = intervalToBucket(context.interval)
  if (bucket === 'daily' || bucket === 'weekly') return null

  const eligibleBars = bars
    .filter((bar) => bar.t <= nowMs)
    .sort((a, b) => a.t - b.t)
  if (eligibleBars.length === 0) return null

  // Group loaded bars by NY calendar date. Previous-day levels are anchored to
  // the latest loaded chart session, not the wall-clock date alone; otherwise a
  // stale/live window crossing midnight can select the wrong prior session.
  const byDate = new Map<string, BarPoint[]>()
  for (const bar of eligibleBars) {
    const d = nyCalendarDate(bar.t)
    let arr = byDate.get(d)
    if (!arr) { byDate.set(d, []); arr = byDate.get(d)! }
    arr.push(bar)
  }

  const dates = Array.from(byDate.keys()).sort()
  const nowCalDate = nyCalendarDate(nowMs)
  let currentCalDate: string | null = null
  for (let i = dates.length - 1; i >= 0; i--) {
    if (dates[i] <= nowCalDate) {
      currentCalDate = dates[i]
      break
    }
  }
  currentCalDate ??= dates[dates.length - 1] ?? null
  if (!currentCalDate) return null

  // Prefer caller-supplied previous-session bars when provided. The chart's
  // 1D range only fetches today's bars, so the in-chart scan below can't find
  // a prior session — the caller fetches it separately and passes it in here.
  let prevHigh: number | null = null
  let prevLow: number | null = null
  if (prevDayBars && prevDayBars.length > 0) {
    for (const bar of prevDayBars) {
      if (!barInSession(bar, RTH)) continue
      if (prevHigh === null || bar.h > prevHigh) prevHigh = bar.h
      if (prevLow === null || bar.l < prevLow) prevLow = bar.l
    }
  } else {
    // Fallback: scan loaded chart bars for the most recent calendar date
    // before the active chart date with a complete RTH session.
    let prevCalDate: string | null = null
    for (let i = dates.length - 1; i >= 0; i--) {
      if (dates[i] >= currentCalDate) continue
      const dayBars = byDate.get(dates[i])!
      if (hasCompleteSessionCoverage(dayBars, context.interval, RTH)) {
        prevCalDate = dates[i]
        break
      }
    }
    if (!prevCalDate) return null
    const prevBars = byDate.get(prevCalDate)!
    for (const bar of prevBars) {
      if (!barInSession(bar, RTH)) continue
      if (prevHigh === null || bar.h > prevHigh) prevHigh = bar.h
      if (prevLow === null || bar.l < prevLow) prevLow = bar.l
    }
  }
  // Pick color / width / style from user config; fall back to defaults.
  const pdIndicator = userConfig.indicators.find(
    (i) => i.type === 'prevDayHighLow',
  ) as UserPrevDayHighLowIndicator | undefined
  const highColor = pdIndicator?.highColor ?? DEFAULT_INDICATOR_COLORS.prevDayHighLow
  const lowColor = pdIndicator?.lowColor ?? DEFAULT_INDICATOR_COLORS.prevDayHighLow
  const width = pdIndicator?.width ?? 1
  const style = pdIndicator?.style ?? 'dashed'
  let segmentFrom: number | null = null
  for (const bar of eligibleBars) {
    if (nyCalendarDate(bar.t) !== currentCalDate) continue
    segmentFrom = bar.t
    break
  }
  if (segmentFrom === null) return null
  const lastEligibleBar = eligibleBars[eligibleBars.length - 1]
  const segmentTo = (lastEligibleBar?.t ?? nowMs) +
    intervalDurationMs(context.interval) * PRICE_LEVEL_RIGHT_EXTENSION_BARS

  const levels: IndicatorPriceLevel[] = []
  const highLevel = makePriceLevel('prevday-high', 'Prev Day High', prevHigh, highColor, width, segmentFrom, segmentTo, style)
  const lowLevel = makePriceLevel('prevday-low', 'Prev Day Low', prevLow, lowColor, width, segmentFrom, segmentTo, style)
  if (highLevel) levels.push(highLevel)
  if (lowLevel) levels.push(lowLevel)
  return levels.length > 0 ? levels : null
}

// ── computeOrb ───────────────────────────────────────────────────────────────

export function computeOrb(
  context: IndicatorContext,
  userConfig: UserIndicatorConfig,
): { levels: IndicatorPriceLevel[]; band: IndicatorBand | null } | null {
  const { bars, nowMs } = context

  // Only for intraday/hourly intervals
  const bucket = intervalToBucket(context.interval)
  if (bucket === 'daily' || bucket === 'weekly') return null

  // Check if opening range is locked (after 09:45 ET)
  const nyHours = toNYHours(nowMs)
  const currentTime = nyHours.h * 60 + nyHours.m
  if (currentTime < OPENING_RANGE.end[0] * 60 + OPENING_RANGE.end[1]) {
    return null
  }

  // Pick color / width / style from user config; fall back to defaults.
  const orbIndicator = userConfig.indicators.find(
    (i) => i.type === 'orb',
  ) as UserOrbIndicator | undefined
  const highColor = orbIndicator?.highColor ?? DEFAULT_INDICATOR_COLORS.orb
  const lowColor = orbIndicator?.lowColor ?? DEFAULT_INDICATOR_COLORS.orb
  const width = orbIndicator?.width ?? 1
  const style = orbIndicator?.style ?? 'dashed'

  const currentCalDate = nyCalendarDate(nowMs)

  // Find bars within the opening range
  let orbHigh: number | null = null
  let orbLow: number | null = null
  let firstOrBar: BarPoint | null = null
  let lastOrBar: BarPoint | null = null

  for (const bar of bars) {
    if (nyCalendarDate(bar.t) !== currentCalDate) continue
    if (!barInSession(bar, OPENING_RANGE)) continue

    if (!firstOrBar) firstOrBar = bar
    lastOrBar = bar

    if (orbHigh === null || bar.h > orbHigh) orbHigh = bar.h
    if (orbLow === null || bar.l < orbLow) orbLow = bar.l
  }

  if (!firstOrBar || !lastOrBar || orbHigh === null || orbLow === null) return null

  // Verify the OR window is fully covered — need bar at the open (09:30) and
  // at least one bar in the tail of the OR (>= OR end - 5 min).
  const firstParts = toNYHours(firstOrBar.t)
  const lastParts = toNYHours(lastOrBar.t)
  const lastBarMinutes = lastParts.h * 60 + lastParts.m
  const orEnd = OPENING_RANGE.end[0] * 60 + OPENING_RANGE.end[1]
  const hasOpenBar = firstParts.h === RTH.start[0] && firstParts.m === RTH.start[1]
  const reachesEnd = lastBarMinutes >= orEnd - 5
  if (!hasOpenBar || !reachesEnd) return null

  // OR lines begin at opening-range end (09:45 ET), not at market open.
  const orEndMs = lastOrBar.t + intervalDurationMs(context.interval)
  const levelToT = Math.max(orEndMs, extendedPriceLevelToT(context))

  const shaded = orbIndicator?.shaded ?? true
  const levels: IndicatorPriceLevel[] = []
  if (orbHigh !== null && isFinite(orbHigh)) {
    levels.push({
      id: 'orb-high', title: 'ORB High', color: highColor,
      width, style, lineVisible: !shaded,
      segments: [{ fromT: orEndMs, toT: levelToT, value: orbHigh }],
    })
  }
  if (orbLow !== null && isFinite(orbLow)) {
    levels.push({
      id: 'orb-low', title: 'ORB Low', color: lowColor,
      width, style, lineVisible: !shaded,
      segments: [{ fromT: orEndMs, toT: levelToT, value: orbLow }],
    })
  }

  // Shaded rectangle: spans 09:45 ET → right edge of chart, bounded by
  // orbLow–orbHigh. Anchor to real bar timestamps; Date.UTC(y,m,d,9,30) is
  // 09:30 UTC, not 09:30 ET, and the resulting time has no bar so
  // timeToCoordinate is null.
  const band: IndicatorBand | null = shaded
    ? {
        id: 'orb-band',
        fromT: orEndMs,
        toT: levelToT,
        topValue: orbHigh,
        bottomValue: orbLow,
        color: highColor,
        opacity: 0.22,
      }
    : null

  return { levels, band }
}

// ── computeOrbBreakouts ──────────────────────────────────────────────────────

export function computeOrbBreakouts(
  context: IndicatorContext,
  orbLevels: { high: number | null; low: number | null },
): IndicatorBand[] | null {
  const { bars, nowMs } = context
  if (orbLevels.high == null && orbLevels.low == null) return null

  const currentCalDate = nyCalendarDate(nowMs)
  const currentRTHStart = RTH.start[0] * 60 + RTH.start[1]
  const currentTime = toNYHours(nowMs).h * 60 + toNYHours(nowMs).m
  if (currentTime < currentRTHStart) return null

  const openingRangeEnd = OPENING_RANGE.end[0] * 60 + OPENING_RANGE.end[1]

  // Compute 20-bar SMA of volume for the volume filter
  const volumes = bars.map(b => b.v)
  const volSma20 = sma(volumes, 20)

  const bands: IndicatorBand[] = []
  let breakoutAboveFired = false
  let breakoutBelowFired = false

  for (let i = 0; i < bars.length; i++) {
    const bar = bars[i]
    if (nyCalendarDate(bar.t) !== currentCalDate) continue
    if (!barInSession(bar, RTH)) continue
    const parts = toNYHours(bar.t)
    const barMinutes = parts.h * 60 + parts.m
    // Skip bars inside the opening range — breakout candidates are strictly after.
    if (barMinutes < openingRangeEnd) continue

    // Volume filter: preceding 20-bar SMA must exceed 1.5x
    // Use the SMA ending at i-1 (the 20 bars *before* the candidate bar).
    const volSmaIdx = i - 1
    const volSma = volSma20[volSmaIdx]
    if (volSma !== null && volSma > 0 && volumes[i] < volSma * 1.5) continue

    // Check breakout above
    if (orbLevels.high !== null && !breakoutAboveFired && bar.c > orbLevels.high) {
      bands.push({
        id: 'orb-breakout-above',
        fromT: bar.t,
        toT: bar.t,
        topValue: orbLevels.high,
        bottomValue: bar.h,
        color: DEFAULT_INDICATOR_COLORS.fillBull,
        opacity: 0.15,
      })
      breakoutAboveFired = true
    }

    // Check breakout below
    if (orbLevels.low !== null && !breakoutBelowFired && bar.c < orbLevels.low) {
      bands.push({
        id: 'orb-breakout-below',
        fromT: bar.t,
        toT: bar.t,
        topValue: bar.l,
        bottomValue: orbLevels.low,
        color: DEFAULT_INDICATOR_COLORS.fillBear,
        opacity: 0.15,
      })
      breakoutBelowFired = true
    }
  }

  return bands.length > 0 ? bands : null
}

// ── computeSessionPercentLabels ──────────────────────────────────────────────

export function computeSessionPercentLabels(
  context: IndicatorContext,
  userConfig?: UserIndicatorConfig,
): IndicatorLabel[] | null {
  const { bars, nowMs, treatLastBarAsClosed } = context
  const bucket = intervalToBucket(context.interval)

  // Only for 5m and 15m intervals
  if (context.interval !== '5m' && context.interval !== '15m') return null
  if (bucket === 'daily' || bucket === 'weekly') return null

  // Only compute if there are enabled indicators (user has indicators active)
  if (!userConfig || userConfig.indicators.length === 0 || !userConfig.indicators.some((i) => i.enabled)) {
    return null
  }

  const currentNyDate = nyDateKey(nowMs)
  const currentRTHStart = RTH.start[0] * 60 + RTH.start[1]
  const currentTime = toNYHours(nowMs).h * 60 + toNYHours(nowMs).m
  if (currentTime < currentRTHStart) return null

  // Find RTH bars for current session
  let rthOpenPrice: number | null = null
  let lastRthBar: BarPoint | null = null
  const rthBars: BarPoint[] = []

  for (const bar of bars) {
    if (nyDateKey(bar.t) !== currentNyDate) continue
    if (!barInSession(bar, RTH)) continue
    rthBars.push(bar)
    if (rthOpenPrice === null) rthOpenPrice = bar.o
    lastRthBar = bar
  }

  if (rthOpenPrice === null || rthOpenPrice === 0 || rthBars.length === 0) return null

  const labels: IndicatorLabel[] = []

  // Active session percent label
  if (lastRthBar) {
    const activeClose = treatLastBarAsClosed
      ? rthBars[rthBars.length - 2]?.c ?? rthOpenPrice
      : lastRthBar.c
    const pct = ((activeClose - rthOpenPrice) / rthOpenPrice) * 100
    labels.push({
      id: 'session-pct-active',
      t: lastRthBar.t,
      value: activeClose,
      text: `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`,
      color: pct >= 0 ? '#10b981' : '#ef4444',
      anchor: 'above',
      align: 'right',
    })
  }

  return labels.length > 0 ? labels : null
}

// ── computeIndicators (orchestrator) ─────────────────────────────────────────

export function computeIndicators(
  context: IndicatorContext,
  userConfig: UserIndicatorConfig,
): IndicatorOutput {
  const output: IndicatorOutput = {
    lines: [],
    priceLevels: [],
    bands: [],
    labels: [],
  }

  // EMA/SMA lines
  const maLines = computeEmaSma(context, userConfig)
  output.lines.push(...maLines)

  // VWAP
  const vwap = computeVwap(context, userConfig)
  if (vwap) output.lines.push(vwap)

  const premarketEnabled = userConfig.indicators.some(
    (indicator) => indicator.type === 'premarketHighLow' && indicator.enabled,
  )
  if (premarketEnabled) {
    const premarketLevels = computePremarketHighLow(context, userConfig)
    if (premarketLevels) output.priceLevels.push(...premarketLevels)
  }

  const prevDayEnabled = userConfig.indicators.some(
    (indicator) => indicator.type === 'prevDayHighLow' && indicator.enabled,
  )
  if (prevDayEnabled) {
    const prevDayLevels = computePrevDayHighLow(context, userConfig)
    if (prevDayLevels) output.priceLevels.push(...prevDayLevels)
  }

  // ORB
  const orbEnabled = userConfig.indicators.some(
    (indicator) => indicator.type === 'orb' && indicator.enabled,
  )
  if (orbEnabled) {
    const orb = computeOrb(context, userConfig)
    if (orb) {
      output.priceLevels.push(...orb.levels)
      if (orb.band) output.bands.push(orb.band)

      // ORB breakouts
      const orbBreakouts = computeOrbBreakouts(context, {
        high: orbLevelsFromLevels(orb.levels, 'high'),
        low: orbLevelsFromLevels(orb.levels, 'low'),
      })
      if (orbBreakouts) output.bands.push(...orbBreakouts)
    }
  }

  // Session percent labels
  const sessionLabels = computeSessionPercentLabels(context, userConfig)
  if (sessionLabels) output.labels.push(...sessionLabels)

  return output
}

/** Extract high/low values from price levels. */
function orbLevelsFromLevels(
  levels: IndicatorPriceLevel[],
  type: 'high' | 'low',
): number | null {
  const id = `orb-${type}`
  const level = levels.find(l => l.id === id)
  if (!level || level.segments.length === 0) return null
  return level.segments[0].value
}
